import { useEffect, useState } from 'react';
import { priceFormat } from '@apps/utils/format-price';
import {
  Box,
  Button,
  MenuItem,
  Select,
  Slider,
  SliderThumb,
  Stack,
  StackProps,
} from '@mui/material';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import isEqual from 'lodash/isEqual';
import last from 'lodash/last';
import numeral from 'numeral';
/**
 *
 * ===========================
 * MAIN
 * ===========================
 */

interface RangeSliderComponentProps {
  options?: { label: string; value: number }[];
  onChange: (item?: [number, number]) => void;
  value?: [number, number];
  filterType?: string;
  displayButton?: boolean;
  containerProps?: StackProps;
}

export const RangeSliderComponent: React.FC<RangeSliderComponentProps> = ({
  onChange,
  value,
  options,
  filterType,
  displayButton,
  containerProps = {},
}) => {
  const { sx: containerSx, ...restContainerProps } = containerProps;
  const [totalValue, setTotalValue] = useState<[number, number]>(
    value?.length === 2
      ? value
      : [options?.[0]?.value || 0, last(options)?.value || 0]
  );

  interface SliderComponentProps extends React.HTMLAttributes<unknown> {}
  const SliderThumbComponent = (props: SliderComponentProps) => {
    const { children, ...other } = props;
    return (
      <SliderThumb {...other}>
        {children}
        <svg
          width="12"
          height="15"
          viewBox="0 0 12 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 5.46838C0 5.1716 0.131823 4.89015 0.359816 4.70015L5.35982 0.533487C5.73066 0.224448 6.26934 0.224449 6.64018 0.533487L11.6402 4.70015C11.8682 4.89015 12 5.1716 12 5.46837V14C12 14.5523 11.5523 15 11 15H1C0.447715 15 0 14.5523 0 14V5.46838Z"
            fill="#31ACBF"
          />
        </svg>
      </SliderThumb>
    );
  };
  const defaultValue = [0, last(options)?.value || 0];

  useEffect(() => {
    setTotalValue(
      value || [options?.[0]?.value || 0, last(options)?.value || 0]
    );
  }, [options, value]);

  const valueLabelFormat = (value: number) =>
    `${numeral(value || 0).format('0,0a')}`;

  const handleDropdownChange = (key: 'min' | 'max', value: number) => {
    if (key === 'min') {
      setTotalValue((prev) => {
        return prev[1] < value ? [prev?.[1], value] : [value, prev[1]];
      });
      onChange(
        totalValue[1] < value
          ? [totalValue?.[1], value]
          : [value, totalValue[1]]
      );
    } else {
      setTotalValue((prev) => {
        return prev[0] > value ? [value, prev[0]] : [prev[0], value];
      });
      onChange(
        totalValue[0] > value ? [value, totalValue[0]] : [totalValue[0], value]
      );
    }
  };

  const handleOnChange = (value: number | number[]) => {
    setTotalValue(value as [number, number]);
    !displayButton && onChange(value as [number, number]);
  };

  const onClickButton = () => {
    onChange(isEqual(totalValue, defaultValue) ? undefined : totalValue);
  };

  const onReset = () => {
    setTotalValue([0, last(options)?.value || 0]);
    onChange(undefined);
  };

  // ======================= VIEWS
  const { sm } = useGetResponsive();
  return (
    <Stack direction="column" sx={{ alignItems: 'center' }}>
      <Stack
        direction="column"
        spacing={{ xs: 0, lg: 2 }}
        {...restContainerProps}
        sx={{
          alignItems: 'center',
          width: '100%',
          p: 5,
          pb: 15,
          pt: { xs: 1, sm: 6 },
          ...containerSx,
        }}
      >
        <Box
          sx={{
            width: '96%',
          }}
        >
          <Slider
            classes={{
              markLabel: 'none',
            }}
            sx={{
              color: '#3a8589',
              padding: '0',
              '& .MuiSlider-markLabel': {
                display: 'none',
              },
              '& .MuiSlider-mark': {
                display: 'none',
              },
              '& .MuiSlider-thumb': {
                height: 15,
                width: 12,
                marginTop: '17px',
                backgroundColor: 'transparent',
                boxShadow: 'none !important',
                '&:before': {
                  boxShadow: 'none',
                },
                '&:after': {
                  height: 15,
                  width: 12,
                  boxShadow: 'none',
                },
                '&:hover': {
                  boxShadow: 'none',
                },
                pointerEvents: !sm ? 'auto' : 'none',
              },
              '& .MuiSlider-valueLabel': {
                paddingBottom: '8px',
                backgroundColor: 'transparent',
                color: '#31ACBF',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '22px',
              },
              '& .MuiSlider-track': {
                height: 8,
                borderRadius: 4,
                backgroundColor: '#31ACBF',
                border: 'none',
              },
              '& .MuiSlider-rail': {
                height: 8,
                borderRadius: 4,
                backgroundColor: '#EDECED',
              },
            }}
            value={totalValue || [0, 0]}
            onChange={(e, value) => {
              handleOnChange(value);
            }}
            getAriaValueText={valueLabelFormat}
            valueLabelDisplay="on"
            aria-labelledby={`range-slider-${filterType}`}
            slots={{
              thumb: SliderThumbComponent,
            }}
            min={options?.[0]?.value || 0}
            max={last(options)?.value || 0}
            marks={options}
            valueLabelFormat={valueLabelFormat}
            step={null}
          />
        </Box>
        <Box sx={{ height: '32px', width: '100%' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Select
              value={totalValue?.[0]}
              variant="outlined"
              onChange={(e) => {
                handleDropdownChange('min', +e.target.value || 0);
              }}
              sx={{
                width: '50%',
                height: '32px',
                zIndex: 1400,
              }}
              MenuProps={{
                sx: {
                  zIndex: 1400,
                },
              }}
            >
              {options?.map((val: any) => (
                <MenuItem key={val.label} value={val.value || 0}>
                  {priceFormat({ price: val?.label })}
                </MenuItem>
              ))}
            </Select>

            <div>-</div>

            <Select
              value={totalValue?.[1]}
              onChange={(e) => {
                handleDropdownChange('max', +e.target.value || 0);
              }}
              fullWidth
              sx={{ width: '50%', height: '32px' }}
              MenuProps={{
                sx: {
                  zIndex: 1400,
                },
              }}
            >
              {options?.map((val) => (
                <MenuItem key={val.label} value={val.value || 0}>
                  {priceFormat({ price: val.label })}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Box>
      </Stack>
      {displayButton && (
        <Stack
          width={'100%'}
          direction={'row'}
          alignItems={'center'}
          position={'absolute'}
          sx={{ background: 'white', bottom: 0 }}
        >
          <Button
            fullWidth
            onClick={onReset}
            variant={'outlined'}
            color="secondary"
            sx={{
              padding: '7px 22px',
              textTransform: 'capitalize',
              borderRadius: 0,
              fontSize: 16,
              fontWeight: 500,
            }}
          >
            Reset
          </Button>
          <Button
            onClick={onClickButton}
            fullWidth
            variant={'contained'}
            color={'secondary'}
            sx={{
              padding: '7px 22px',
              textTransform: 'capitalize',
              borderRadius: 0,
              fontSize: 16,
              fontWeight: 500,
            }}
          >
            Apply
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default RangeSliderComponent;
